import { type Address, type Hash, parseUnits, formatUnits, encodeFunctionData, erc20Abi } from 'viem';
import { PublicKey } from 'casper-js-sdk';

import type { BridgeToken } from 'config';
import type { NetworkType } from 'contexts';
import type { Chain } from 'types';

// ABI fragments for the MultiSigERC20Locker contract.
// Source commitments go through the exposure-control entry points (locker V18, specification
// section 8.1): the route names the asset, both chains and the operation; the call returns the
// committed transfer's id. `recipientKind` is the specification's address kind
// (2 = Casper account, 3 = Casper contract package); the locker refuses any other kind or a
// zero value (`InvalidRecipient`). `msg.value` must equal `bridgeFee()` exactly.
export const multiSigLockerAbi = [
  // EVM-origin asset: lock ERC-20 custody on a lock-mint route.
  {
    name: 'lock',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      { name: 'routeId', type: 'bytes32' },
      { name: 'amount', type: 'uint256' },
      { name: 'recipientKind', type: 'uint8' },
      { name: 'recipientValue', type: 'bytes32' },
    ],
    outputs: [{ name: 'transferId', type: 'bytes32' }],
  },
  // Casper-origin asset: burn its wrapped ERC-20 on a burnWrapped-unlock route.
  {
    name: 'burnWrapped',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      { name: 'routeId', type: 'bytes32' },
      { name: 'amount', type: 'uint256' },
      { name: 'recipientKind', type: 'uint8' },
      { name: 'recipientValue', type: 'bytes32' },
    ],
    outputs: [{ name: 'transferId', type: 'bytes32' }],
  },
  {
    name: 'isTokenRegistered',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'token', type: 'address' }],
    outputs: [{ type: 'bool' }],
  },
  // Chain-configured bridge fee in native wei. MUST be read live and attached as
  // msg.value: overpaying when fee=0 reverts with UnexpectedNativeValue (MS-20),
  // underpaying when fee>0 reverts with InsufficientBridgeFee.
  {
    name: 'bridgeFee',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'getLockedBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'token', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  // The source refusals of a route commitment (`LockerExposureExtension` and the gateway's
  // `EnforcedPause`), so a reverted call decodes to the contract's own error name.
  ...([
    ['CommitmentsNotActive', ['bytes32']],
    ['CommitmentsPaused', ['bytes32', 'uint256']],
    ['RepresentationTokenNotSet', ['bytes32']],
    ['AmountOutOfRange', ['uint256', 'uint256', 'uint256']],
    ['CustodyCeilingExceeded', ['uint256', 'uint256', 'uint256']],
    ['RouteAllocationExceeded', ['uint256', 'uint256', 'uint256']],
    ['RepresentationCeilingExceeded', ['uint256', 'uint256', 'uint256']],
    ['LinkNotSet', ['bytes']],
    ['LinkMismatch', ['bytes32']],
    ['EnforcedPause', ['uint256']],
    ['InsufficientBridgeFee', ['uint256', 'uint256']],
    ['UnexpectedNativeValue', ['uint256']],
    ['InvalidRecipient', []],
    ['RouteNotRegistered', ['bytes32']],
    ['WrongOperation', ['bytes32', 'uint8']],
  ] as const).map(([name, types]) => ({
    name,
    type: 'error' as const,
    inputs: types.map((type, i) => ({ name: `arg${i}`, type })),
  })),
  // A source commitment (specification sections 8.1 and 11.1); `sendId` joins it to the
  // gateway message that carries the transfer.
  {
    name: 'TransferCommitted',
    type: 'event',
    inputs: [
      { name: 'transferId', type: 'bytes32', indexed: true },
      { name: 'routeId', type: 'bytes32', indexed: true },
      { name: 'sourceSequence', type: 'uint64', indexed: false },
      { name: 'amount', type: 'uint256', indexed: false },
      { name: 'policyVersion', type: 'uint256', indexed: false },
      { name: 'sourceCommitmentTimeMs', type: 'uint64', indexed: false },
      { name: 'sendId', type: 'bytes32', indexed: false },
    ],
  },
] as const;

/**
 * Get the locker contract address for a specific chain
 * @param chain - The chain to get the locker address for. If not provided, falls back to config.
 */
export const getLockerAddress = (chain?: Chain): Address => {
  // If chain is provided, use chain-specific locker address
  if (chain) {
    const address = chain.lockerAddress;
    if (!address || address === '0x0000000000000000000000000000000000000000') {
      throw new Error(`MultiSigERC20Locker address not configured for chain ${chain.name}`);
    }

    return address as Address;
  }

  // Fallback to config for backward compatibility
  const address = config.eth_multisig_locker;
  if (!address || address === '0x0000000000000000000000000000000000000000') {
    throw new Error('MultiSigERC20Locker address not configured');
  }

  return address as Address;
};

/**
 * Get the locker address for a chain by ID
 * This is a convenience function for when you have a chain ID but not the full chain object
 */
export const getLockerAddressForChain = (chain: Chain): Address => {
  return getLockerAddress(chain);
};

// Get the TokenFactory contract hash from config
export const getTokenFactoryHash = (): string => {
  const hash = config.casper_token_factory;
  if (!hash || hash === 'hash-0000000000000000000000000000000000000000000000000000000000000000') {
    throw new Error('TokenFactory contract hash not configured');
  }

  return hash;
};

/**
 * Convert a Casper public key or account hash to bytes32 format for Ethereum
 *
 * Casper uses different formats:
 * - Public key: 01{64 hex chars} (ed25519) or 02{66 hex chars} (secp256k1)
 * - Account hash: account-hash-{64 hex chars}
 *
 * The bridge contract expects the account hash (32 bytes).
 * Uses casper-js-sdk's PublicKey.accountHash() for proper conversion.
 */
export const casperAccountToBytes32 = (input: string): `0x${string}` => {
  let cleanInput = input;

  // If it's already an account hash, extract the hex part
  if (cleanInput.startsWith('account-hash-')) {
    cleanInput = cleanInput.replace('account-hash-', '');
    if (cleanInput.length !== 64) {
      throw new Error(`Invalid account hash length: expected 64 chars, got ${cleanInput.length}`);
    }

    return `0x${cleanInput}` as `0x${string}`;
  }

  if (cleanInput.startsWith('hash-')) {
    cleanInput = cleanInput.replace('hash-', '');
    if (cleanInput.length !== 64) {
      throw new Error(`Invalid hash length: expected 64 chars, got ${cleanInput.length}`);
    }

    return `0x${cleanInput}` as `0x${string}`;
  }

  // Check if it's a public key (starts with 01 or 02)
  // ed25519: 01 prefix + 64 hex chars = 66 total
  // secp256k1: 02 prefix + 66 hex chars = 68 total
  const isEd25519PublicKey = cleanInput.length === 66 && cleanInput.startsWith('01');
  const isSecp256k1PublicKey = cleanInput.length === 68 && cleanInput.startsWith('02');

  if (isEd25519PublicKey || isSecp256k1PublicKey) {
    // Use casper-js-sdk to properly compute account hash from public key
    const publicKey = PublicKey.fromHex(cleanInput);
    const accountHash = publicKey.accountHash();
    // accountHash.toPrefixedString() returns "account-hash-{hex}"
    // We just need the hex part for bytes32
    const accountHashHex = accountHash.toPrefixedString().replace('account-hash-', '');

    return `0x${accountHashHex}` as `0x${string}`;
  }

  // If it's exactly 64 chars, assume it's already an account hash
  if (cleanInput.length === 64) {
    return `0x${cleanInput}` as `0x${string}`;
  }

  throw new Error(`Invalid Casper address format: expected public key (66/68 chars) or account hash (64 chars), got ${cleanInput.length} chars`);
};

/** Specification address kinds of a Casper recipient (`ExposureIds.ADDRESS_CASPER_*`). */
export const CASPER_RECIPIENT_KIND_ACCOUNT = 2;
export const CASPER_RECIPIENT_KIND_PACKAGE = 3;

/**
 * The address kind of a Casper recipient for the route entry points: `hash-` prefixed inputs
 * are contract packages (3); public keys, `account-hash-` prefixed and bare 64-char inputs are
 * accounts (2). The value is `casperAccountToBytes32` of the same input.
 */
export const casperRecipientKindOf = (input: string): number =>
  input.startsWith('hash-') ? CASPER_RECIPIENT_KIND_PACKAGE : CASPER_RECIPIENT_KIND_ACCOUNT;

/**
 * Convert an Ethereum address to the format expected by Casper
 * Ethereum addresses are 20 bytes, we need to pad to 32 bytes
 */
export const ethereumAddressToBytes32 = (address: string): string => {
  // Remove 0x prefix and pad to 32 bytes (64 hex chars)
  const cleanAddress = address.replace('0x', '').toLowerCase();

  return cleanAddress.padStart(64, '0');
};

/**
 * Calculate the approval amount needed for a bridge operation
 * We use exact amount approval as specified
 */
export const calculateApprovalAmount = (
  amount: string,
  decimals: number
): bigint => {
  return parseUnits(amount, decimals);
};

/**
 * Format a raw amount to human-readable format
 */
export const formatAmount = (
  amount: bigint,
  decimals: number
): string => {
  return formatUnits(amount, decimals);
};

/**
 * Generate the route-based lock function data for Ethereum
 */
export const encodeLockData = (
  routeId: `0x${string}`,
  amount: bigint,
  recipientKind: number,
  recipientValue: `0x${string}`
): `0x${string}` => {
  return encodeFunctionData({
    abi: multiSigLockerAbi,
    functionName: 'lock',
    args: [routeId, amount, recipientKind, recipientValue],
  });
};

/**
 * Generate the ERC-20 approve function data
 */
export const encodeApproveData = (
  spender: Address,
  amount: bigint
): `0x${string}` => {
  return encodeFunctionData({
    abi: erc20Abi,
    functionName: 'approve',
    args: [spender, amount],
  });
};

/**
 * Bridge direction and flow helpers
 */
export const getBridgeDirection = (
  sourceNetwork: NetworkType
): 'eth2casper' | 'casper2eth' => {
  return sourceNetwork === 'ethereum' ? 'eth2casper' : 'casper2eth';
};

/**
 * Get the steps for a bridge transaction based on direction
 */
export interface BridgeStep {
  id: string;
  label: string;
  description: string;
}

/**
 * Chain finality information for calculating wait times
 */
export interface ChainFinalityInfo {
  chainName: string;        // Display name (e.g., "Polygon Amoy", "Sepolia")
  confirmations: number;    // Number of confirmations required
  blockTime: number;        // Block time in seconds
}

/**
 * The relayer enforces a global confirmation-depth floor before attesting:
 * requiredConfirmations = max(FINALITY_CONFIRMATIONS, per-chain confirmations)
 * (see cspr-bridge cli/src/workers/evm2casper.ts requiredConfirmationsFor, MS-09/MS-10).
 * FINALITY_CONFIRMATIONS defaults to 64 in the relayer env. The UI must mirror
 * that floor or it promises finality times the relayer will never honor
 * (e.g. Sepolia's nominal 6 confs → "< 3 min" vs the real 64 × 12s ≈ 13 min).
 */
export const RELAYER_FINALITY_FLOOR = 64;

/**
 * Calculate estimated finality time for a chain
 * For Casper: < 1 minute (Zug consensus — immediate finality)
 * For EVM chains: ceil(1.5 + ((max(floor, confirmations) * blockTime) / 60)) minutes
 */
export const calculateFinalityTime = (chain: ChainFinalityInfo | null, isCasper: boolean): string => {
  if (isCasper) {
    return '< 1 minute';
  }

  if (!chain) {
    return '~14 minutes'; // fallback for unknown chains (64 × 12s + margin)
  }

  const effectiveConfirmations = Math.max(RELAYER_FINALITY_FLOOR, chain.confirmations);
  const minutes = Math.ceil(1.5 + ((effectiveConfirmations * chain.blockTime) / 60));

  return `~${minutes} minutes`;
};

// EVM-origin tokens: EVM → Casper (approve → lock ERC-20 → mint wrapped CEP-18)
export const getEthToCasperSteps = (chain?: ChainFinalityInfo): BridgeStep[] => {
  const chainName = chain?.chainName || 'EVM';
  const finalityTime = calculateFinalityTime(chain || null, false);

  return [
    {
      id: 'approval',
      label: 'Approve Token',
      description: 'Approve the bridge contract to spend your tokens',
    },
    {
      id: 'lock',
      label: 'Lock Tokens',
      description: 'Lock your tokens in the bridge contract',
    },
    {
      id: 'finality',
      label: 'Wait for Finality',
      description: `Waiting for ${chainName} finality (${finalityTime})`,
    },
    {
      id: 'attestation',
      label: 'Relayer Attestation',
      description: 'Relayers are verifying and signing the transaction',
    },
    {
      id: 'mint',
      label: 'Mint on Casper',
      description: 'CEP-18 tokens are being minted to your Casper address',
    },
  ];
};

// EVM-origin tokens: Casper → EVM (burn wrapped CEP-18 → unlock native ERC-20)
export const getCasperToEthSteps = (chain?: ChainFinalityInfo): BridgeStep[] => {
  const chainName = chain?.chainName || 'EVM';
  const finalityTime = calculateFinalityTime(null, true); // Casper finality

  return [
    {
      id: 'burn',
      label: 'Burn Tokens',
      description: 'Burn your wrapped CEP-18 tokens on Casper',
    },
    {
      id: 'finality',
      label: 'Wait for Finality',
      description: `Waiting for Casper finality (${finalityTime})`,
    },
    {
      id: 'attestation',
      label: 'Relayer Attestation',
      description: 'Relayers are verifying and signing the transaction',
    },
    {
      id: 'unlock',
      label: `Unlock on ${chainName}`,
      description: `Native ERC-20 tokens are being unlocked to your ${chainName} address`,
    },
  ];
};

// ============================================
// Casper-origin token flows
// ============================================

// Casper-origin tokens: Casper → EVM (approve → lock native CEP-18 → mint wrapped ERC-20)
export const getCasperOriginCasperToEvmSteps = (chain?: ChainFinalityInfo): BridgeStep[] => {
  const chainName = chain?.chainName || 'EVM';
  const finalityTime = calculateFinalityTime(null, true); // Casper finality

  return [
    {
      id: 'approve_native',
      label: 'Approve Tokens',
      description: 'Approve the bridge to spend your CEP-18 tokens',
    },
    {
      id: 'lock_native',
      label: 'Lock Tokens',
      description: 'Lock your native CEP-18 tokens on Casper',
    },
    {
      id: 'finality',
      label: 'Wait for Finality',
      description: `Waiting for Casper finality (${finalityTime})`,
    },
    {
      id: 'attestation',
      label: 'Relayer Attestation',
      description: 'Relayers are verifying and signing the transaction',
    },
    {
      id: 'mint_wrapped',
      label: `Mint on ${chainName}`,
      description: `Wrapped ERC-20 tokens are being minted to your ${chainName} address`,
    },
  ];
};

// Casper-origin tokens: EVM → Casper (approve → burn wrapped ERC-20 → unlock native CEP-18)
export const getCasperOriginEvmToCasperSteps = (chain?: ChainFinalityInfo): BridgeStep[] => {
  const chainName = chain?.chainName || 'EVM';
  const finalityTime = calculateFinalityTime(chain || null, false);

  return [
    {
      id: 'approval',
      label: 'Approve Token',
      description: 'Approve the bridge contract to spend your wrapped tokens',
    },
    {
      id: 'burn_wrapped',
      label: 'Burn Wrapped Tokens',
      description: 'Burn your wrapped ERC-20 tokens',
    },
    {
      id: 'finality',
      label: 'Wait for Finality',
      description: `Waiting for ${chainName} finality (${finalityTime})`,
    },
    {
      id: 'attestation',
      label: 'Relayer Attestation',
      description: 'Relayers are verifying and signing the transaction',
    },
    {
      id: 'unlock_native',
      label: 'Unlock on Casper',
      description: 'Native CEP-18 tokens are being unlocked to your Casper address',
    },
  ];
};

/**
 * Get the estimated time for a bridge operation
 */
export const getEstimatedBridgeTime = (direction: 'eth2casper' | 'casper2eth'): string => {
  return direction === 'eth2casper' ? '15-20 minutes' : '5-10 minutes';
};

/**
 * Bridge transaction status type
 */
export type BridgeStatus =
  | 'idle'
  | 'pending-approval'
  | 'approving'
  | 'pending-lock'
  | 'locking'
  | 'pending-burn'
  | 'burning'
  | 'waiting-finality'
  | 'waiting-attestation'
  | 'minting'
  | 'unlocking'
  | 'completed'
  | 'failed';

/**
 * Parse a lock event from a transaction receipt
 */
export interface LockEventData {
  token: Address;
  sender: Address;
  amount: bigint;
  casperRecipient: `0x${string}`;
  nonce: bigint;
  transactionHash: Hash;
  blockNumber: bigint;
}

/**
 * Validate bridge parameters
 */
export const validateBridgeParams = (
  amount: string,
  token: BridgeToken | null,
  sourceNetwork: NetworkType,
  casperAddress: string | null,
  ethereumAddress: string | null
): { valid: boolean; error?: string } => {
  if (!token) {
    return { valid: false, error: 'Please select a token' };
  }

  if (!amount || parseFloat(amount) <= 0) {
    return { valid: false, error: 'Please enter a valid amount' };
  }

  if (sourceNetwork === 'ethereum' && !casperAddress) {
    return { valid: false, error: 'Casper wallet not connected' };
  }

  if (sourceNetwork === 'casper' && !ethereumAddress) {
    return { valid: false, error: 'Ethereum wallet not connected' };
  }

  // Check minimum amount (could be token-specific)
  const amountNum = parseFloat(amount);
  if (amountNum < 0.000001) {
    return { valid: false, error: 'Amount too small' };
  }

  return { valid: true };
};

/**
 * Generate a unique transaction ID
 */
export const generateTransactionId = (): string => {
  return `bridge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Explorer URLs
 */

/**
 * Get explorer URL for an EVM chain transaction
 * @param hash - Transaction hash
 * @param chain - The chain to get the explorer URL for. If not provided, falls back to Sepolia.
 */
export const getEvmExplorerUrl = (hash: string, chain?: Chain): string => {
  if (chain?.explorerUrl) {
    return `${chain.explorerUrl}/tx/${hash}`;
  }

  // Fallback to Sepolia etherscan
  return `https://sepolia.etherscan.io/tx/${hash}`;
};

/**
 * Get explorer URL for an EVM chain address
 */
export const getEvmAddressExplorerUrl = (address: string, chain?: Chain): string => {
  if (chain?.explorerUrl) {
    return `${chain.explorerUrl}/address/${address}`;
  }

  // Fallback to Sepolia etherscan
  return `https://sepolia.etherscan.io/address/${address}`;
};

/**
 * @deprecated Use getEvmExplorerUrl instead
 */
export const getEthereumExplorerUrl = (hash: string): string => {
  // Sepolia etherscan
  return `https://sepolia.etherscan.io/tx/${hash}`;
};

export const getCasperExplorerUrl = (hash: string): string => {
  const baseUrl = config.cspr_live_url || 'https://testnet.cspr.live';

  return `${baseUrl}/deploy/${hash}`;
};

/**
 * Get explorer URL based on network type and optionally chain
 */
export const getExplorerUrl = (hash: string, network: NetworkType, chain?: Chain): string => {
  if (network === 'ethereum') {
    return getEvmExplorerUrl(hash, chain);
  }

  return getCasperExplorerUrl(hash);
};
